<template>
  <div class="flex flex-col h-full bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 p-4">
      <div v-if="activeConversation" class="flex items-center justify-between">
        <div>
          <h2 class="font-semibold text-lg text-gray-900">
            {{ activeConversation.contact_name || 'Sem nome' }}
          </h2>
          <p class="text-sm text-gray-500">
            {{ formatPhone(activeConversation.contact_phone) }}
          </p>
        </div>
        
        <div class="flex items-center gap-2">
          <span
            v-if="activeConversation.lead_status"
            :class="['text-xs px-3 py-1 rounded-full font-medium', statusClass(activeConversation.lead_status)]"
          >
            {{ leadStatusLabel(activeConversation.lead_status) }}
          </span>
        </div>
      </div>
      <div v-else class="text-gray-400 text-center">
        <ChatBubbleLeftRightIcon class="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>Selecione uma conversa para começar</p>
      </div>
    </div>
    
    <!-- AI Agent Banner (source != manual) -->
    <div v-if="activeConversation && activeConversation.source !== 'manual'" class="bg-indigo-50 border-b border-indigo-100 p-3 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="bg-indigo-100 p-1.5 rounded-full">🤖</span>
        <div>
          <p class="text-sm font-medium text-indigo-900">Agente de IA em atendimento</p>
          <p class="text-xs text-indigo-700">Assuma o atendimento quando quiser continuar você mesmo.</p>
        </div>
      </div>
      <button
        @click="takeOverConversation"
        :disabled="takingOver"
        class="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md font-medium hover:bg-indigo-700 transition disabled:opacity-50"
      >
        <span v-if="takingOver">Assumindo...</span>
        <span v-else>Assumir Atendimento</span>
      </button>
    </div>

    <!-- Human Agent Banner (source == manual) -->
    <div v-if="activeConversation && activeConversation.source === 'manual'" class="bg-green-50 border-b border-green-100 p-3 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="bg-green-100 p-1.5 rounded-full">👤</span>
        <div>
          <p class="text-sm font-medium text-green-900">Atendimento humano ativo</p>
          <p class="text-xs text-green-700">Você está no controle. Devolva à IA quando quiser.</p>
        </div>
      </div>
      <button
        @click="releaseToAI"
        :disabled="releasingToAI"
        class="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md font-medium hover:bg-green-700 transition disabled:opacity-50"
      >
        <span v-if="releasingToAI">Devolvendo...</span>
        <span v-else>Devolver à IA</span>
      </button>
    </div>

    <!-- Messages -->
    <div 
      ref="messagesContainer" 
      class="flex-1 overflow-y-auto p-4 space-y-4"
      @scroll="handleScroll"
    >
      <div v-if="loading" class="flex items-center justify-center h-full">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
      
      <div v-else-if="messages.length === 0 && activeConversation" class="text-center text-gray-400 mt-12">
        <EnvelopeIcon class="w-16 h-16 mx-auto mb-3 text-gray-300" />
        <p>Nenhuma mensagem ainda</p>
        <p class="text-sm mt-1">Envie a primeira mensagem para iniciar a conversa</p>
      </div>
      
      <MessageBubble
        v-for="message in messages"
        :key="message.id"
        :message="message"
      />
    </div>
    
    <!-- Message Input -->
    <div v-if="activeConversation" class="bg-white border-t border-gray-200 p-4">
      <form @submit.prevent="sendMessage" class="flex items-end gap-3">
        <textarea
          v-model="newMessage"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.shift.enter="handleShiftEnter"
          placeholder="Digite sua mensagem..."
          rows="1"
          class="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-32"
          :disabled="sending"
        />
        <button
          type="submit"
          :disabled="!newMessage.trim() || sending"
          class="px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
        >
          <PaperAirplaneIcon class="w-5 h-5" />
          <span v-if="!sending">Enviar</span>
          <span v-else>Enviando...</span>
        </button>
      </form>
      <p class="text-xs text-gray-500 mt-2">
        Pressione Enter para enviar, Shift+Enter para nova linha
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import axios from 'axios'
import { 
  ChatBubbleLeftRightIcon, 
  EnvelopeIcon,
  PaperAirplaneIcon 
} from '@heroicons/vue/24/outline'
import { useConversationsStore } from '@/stores/conversations'
import { useMessagesStore } from '@/stores/messages'
import { useUserStore } from '@/stores/user'
import MessageBubble from './MessageBubble.vue'

const conversationsStore = useConversationsStore()
const messagesStore = useMessagesStore()
const userStore = useUserStore()

const messagesContainer = ref(null)
const newMessage = ref('')
const sending = ref(false)
const takingOver = ref(false)
const releasingToAI = ref(false)
const autoScroll = ref(true)
const messagePollingInterval = ref(null)

const activeConversation = computed(() => conversationsStore.activeConversation)
const messages = computed(() => messagesStore.messages)
const loading = computed(() => messagesStore.loading)

watch(() => conversationsStore.activeConversationId, async (newId) => {
  // Parar polling da conversa anterior
  if (messagePollingInterval.value) {
    clearInterval(messagePollingInterval.value)
    messagePollingInterval.value = null
  }

  if (newId) {
    messagesStore.clearMessages()
    await messagesStore.fetchMessages(newId)
    await nextTick()
    scrollToBottom()

    // Polling direto a cada 5s — independente do assigned_to e do status das mensagens
    messagePollingInterval.value = setInterval(async () => {
      if (conversationsStore.activeConversationId) {
        await messagesStore.fetchMessages(conversationsStore.activeConversationId)
        await nextTick()
        if (autoScroll.value) scrollToBottom()
      }
    }, 5000)
  }
}, { immediate: true })

watch(() => messages.value.length, async () => {
  if (autoScroll.value) {
    await nextTick()
    scrollToBottom()
  }
})

onUnmounted(() => {
  if (messagePollingInterval.value) {
    clearInterval(messagePollingInterval.value)
  }
})

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

function handleScroll() {
  if (!messagesContainer.value) return
  
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value
  const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
  autoScroll.value = isAtBottom
}

async function takeOverConversation() {
  if (!activeConversation.value || takingOver.value) return
  
  takingOver.value = true
  try {
    const user = userStore.currentUser
    const userName = user?.name || 'um atendente'
    const userId = user?.id || 1

    // Mensagem personalizada solicitada
    const autoMessage = `Olá! A partir de agora *${userName}* irá dar continuidade em seu atendimento para agendamento de seu serviço.`

    // 1. Chamar API
    await axios.post(`/api/conversations/${activeConversation.value.id}/takeover`, {
      user_id: userId,
      message: autoMessage,
      source: 'manual' // Adiciona o campo source para a API
    })
    
    // 2. Atualizar estado local
    // Forçar atualização do objeto activeConversation para remover o banner
    activeConversation.value.source = 'manual'
    
    // 3. Atualizar mensagens (para aparecer a mensagem de saudação)
    await messagesStore.fetchMessages(activeConversation.value.id)
    
  } catch (error) {
    console.error('Erro ao assumir conversa:', error)
    alert('Erro ao assumir atendimento.')
  } finally {
    takingOver.value = false
  }
}

async function releaseToAI() {
  if (!activeConversation.value || releasingToAI.value) return

  releasingToAI.value = true
  try {
    await axios.post(`/api/conversations/${activeConversation.value.id}/release-to-ai`)
    activeConversation.value.source = 'whatsapp'
  } catch (error) {
    console.error('Erro ao devolver conversa à IA:', error)
    alert('Erro ao devolver à IA.')
  } finally {
    releasingToAI.value = false
  }
}

async function sendMessage() {
  if (!newMessage.value.trim() || sending.value || !activeConversation.value) return
  
  sending.value = true
  const messageContent = newMessage.value.trim()
  newMessage.value = ''
  
  try {
    await messagesStore.sendMessage({
      conversation_id: activeConversation.value.id,
      content: messageContent
    })
    
    autoScroll.value = true
    await nextTick()
    scrollToBottom()
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error)
    alert('Erro ao enviar mensagem. Tente novamente.')
    newMessage.value = messageContent // Restore message on error
  } finally {
    sending.value = false
  }
}

function handleShiftEnter(event) {
  // Allow Shift+Enter to create new line
  // preventDefault is not called, so the default behavior works
}

function formatPhone(phone) {
  if (!phone) return ''
  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '')
  // Format as Brazilian phone: (XX) XXXXX-XXXX
  if (cleaned.length === 11) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`
  }
  return phone
}

function leadStatusLabel(status) {
  const labels = {
    novo: 'Novo',
    contatado: 'Contatado',
    qualificado: 'Qualificado',
    orcamento: 'Orçamento',
    negociacao: 'Negociação',
    ganho: 'Ganho',
    perdido: 'Perdido',
    em_atendimento: 'Em Atendimento'
  }
  return labels[status] || status
}

function statusClass(status) {
  const classes = {
    novo: 'bg-gray-100 text-gray-700',
    contatado: 'bg-blue-100 text-blue-700',
    qualificado: 'bg-green-100 text-green-700',
    orcamento: 'bg-yellow-100 text-yellow-700',
    negociacao: 'bg-orange-100 text-orange-700',
    ganho: 'bg-green-200 text-green-800',
    perdido: 'bg-red-100 text-red-700',
    em_atendimento: 'bg-blue-100 text-blue-700'
  }
  return classes[status] || 'bg-gray-100 text-gray-700'
}
</script>
